import { Observable } from 'rxjs';
import { PaginablePayloadApiResponse } from './types.domain';
import { KycStatus } from './digital-id-service.domain';
import { KybResultStatus } from './kyb-key-people.domain';
import {
  PrivacyConsentFormType,
  PrivacyConsentStatus,
  StageNameType,
} from './application-service.domain';

/** The caller, resolved on the server so the same filter works for whoever uses it. */
export const AML_OFFICER_FILTER_ME = 'ME';
export const AML_OFFICER_FILTER_UNASSIGNED = 'UNASSIGNED';
/** One entry of the officer filter: the caller, unassigned, or an officer's UserId. */
export type AmlOfficerFilterValue = typeof AML_OFFICER_FILTER_ME | typeof AML_OFFICER_FILTER_UNASSIGNED | number;

export interface AmlFilterOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Named rules that sit alongside the status boxes rather than filling them in.
 * RISK_ABOVE_LOW: any individual or entity has a Medium, High or Unacceptable risk.
 * REVIEW_REQUIRED: any individual's KYC is under review, failed or errored, or any entity's latest
 * KYB run failed. ALL adds no condition.
 */
export type AmlCheckFilter = 'RISK_ABOVE_LOW' | 'REVIEW_REQUIRED' | 'ALL';
export const AML_CHECK_FILTERS: AmlCheckFilter[] = ['RISK_ABOVE_LOW', 'REVIEW_REQUIRED', 'ALL'];
export const AML_DEFAULT_CHECK: AmlCheckFilter = 'ALL';
export const AML_CHECK_OPTIONS: AmlFilterOption<AmlCheckFilter>[] = [
  { value: 'RISK_ABOVE_LOW', label: 'Risk above Low' },
  { value: 'REVIEW_REQUIRED', label: 'Review required' },
  { value: 'ALL', label: 'All' },
];

/** Each is matched against every individual (privacy, KYC) or entity (KYB) of an application; any one is enough. */
export type AmlPrivacyFilter = 'NOT_SENT' | 'AWAITING_SIGNATURE' | 'COMPLETED' | 'EXPIRED' | 'DECLINED' | 'DISPATCH_FAILED';
export const AML_PRIVACY_OPTIONS: AmlFilterOption<AmlPrivacyFilter>[] = [
  { value: 'NOT_SENT', label: 'Not sent' },
  { value: 'AWAITING_SIGNATURE', label: 'Awaiting signature' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'DISPATCH_FAILED', label: 'Dispatch failed' },
];

export type AmlKycFilter = 'NOT_STARTED' | 'SMS_SENT' | 'UNDER_REVIEW' | 'VERIFIED' | 'INCOMPLETE' | 'FAILED' | 'ERROR';
export const AML_KYC_OPTIONS: AmlFilterOption<AmlKycFilter>[] = [
  { value: 'NOT_STARTED', label: 'Not started' },
  { value: 'SMS_SENT', label: 'SMS sent' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'VERIFIED', label: 'Verification successful' },
  { value: 'INCOMPLETE', label: 'Verification incomplete' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'ERROR', label: 'System error' },
];

export type AmlKybFilter = 'NOT_RUN' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
export const AML_KYB_OPTIONS: AmlFilterOption<AmlKybFilter>[] = [
  { value: 'NOT_RUN', label: 'Not run' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
];

/** NONE is an application with no risk result yet. */
export type AmlRiskFilter = 'LOW' | 'MEDIUM' | 'HIGH' | 'UNACCEPTABLE' | 'NONE';
export const AML_RISK_OPTIONS: AmlFilterOption<AmlRiskFilter>[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'UNACCEPTABLE', label: 'Unacceptable' },
  { value: 'NONE', label: 'Not assessed' },
];

/**
 * Never listed or shown. Privacy and KYC are not issued until an application is submitted, so a
 * draft has nothing to follow up; Lead, Prospect, Proposal and Application Received are
 * pre-credit Salesforce stages that only legacy test applications carry.
 */
export const AML_EXCLUDED_STAGES: StageNameType[] = ['Draft', 'Lead', 'Prospect', 'Proposal', 'Application Received'];

/** Every other stage in pipeline order, which the stage filter lists and the stage sort follows. */
export const AML_STAGE_OPTIONS: StageNameType[] = [
  'Submitted to Credit',
  'Waiting for Bank Statements', 'More Information', 'Credit Approved', 'Documentation', 'Docs Out',
  'Settlement Pending', 'QA', 'Settlement', 'Closed Won', 'Closed Lost',
];
/** Every stage still in progress. */
export const AML_DEFAULT_STAGES: StageNameType[] = AML_STAGE_OPTIONS
  .filter(stage => stage !== 'Closed Won' && stage !== 'Closed Lost');

/** FrankieOne risk levels counted as "above Low". Compared case-insensitively. */
export const AML_ELEVATED_RISK_LEVELS = ['MEDIUM', 'HIGH', 'UNACCEPTABLE'] as const;

const AML_RISK_ORDER = ['LOW', ...AML_ELEVATED_RISK_LEVELS];

/** Higher is riskier; -1 for a missing or unrecognised level. */
export const amlRiskRank = (riskLevel: string | null | undefined): number =>
  AML_RISK_ORDER.indexOf((riskLevel ?? '').trim().toUpperCase());

/**
 * Sent as the pagination `sorts`, first entry only. Highest risk is worked out for every matching
 * application at query time, so it is noticeably slower than the others.
 */
export type AmlDashboardSortProp = 'applicationId' | 'createdAt' | 'stage' | 'highestRisk';
export const AML_DASHBOARD_SORT_PROPS: AmlDashboardSortProp[] = ['applicationId', 'createdAt', 'stage', 'highestRisk'];
export interface AmlDashboardSort {
  prop: AmlDashboardSortProp;
  dir: 'asc' | 'desc';
}

/** The CSV export is a full download rather than a page, so it is capped instead of paginated. */
export const AML_DASHBOARD_CSV_MAX_ROWS = 5000;

export interface AmlDashboardFilters {
  /** Matches any of the entries. Omitted or empty matches every officer, assigned or not. */
  officers?: AmlOfficerFilterValue[];
  /** Defaults to AML_DEFAULT_CHECK. */
  checks?: AmlCheckFilter;
  /** Each omitted or empty list adds no condition. Lists combine with AND; values within a list with OR. */
  privacy?: AmlPrivacyFilter[];
  kyc?: AmlKycFilter[];
  kyb?: AmlKybFilter[];
  /** Matches the application's highest risk, the value its column shows. */
  risks?: AmlRiskFilter[];
  /** Matches any of the stages. Omitted or empty matches every stage in AML_STAGE_OPTIONS. */
  stages?: StageNameType[];
  /** Matches the application ID exactly, or part of the broker app ID, borrowing entity or any individual's name. */
  search?: string;
  /** Inclusive bounds on Application.CreateTime, as ISO 8601 instants. */
  startDate?: string;
  endDate?: string;
}

export interface AmlDashboardIndividual {
  applicationIndividualId: number;
  /** ApplicationIndividual.legacyId, which the privacy and KYC endpoints address individuals by. */
  individualId: string;
  name: string;
  roles: string;
  privacyStatus: PrivacyConsentStatus | null;
  /** Current form accepted and not past CONSENT_MAX_AGE_DAYS. */
  privacyCompleted: boolean;
  privacyFirstSentAt: string | null;
  privacyLastSentAt: string | null;
  privacySendCount: number;
  privacySignedAt: string | null;
  /** Null for a legacy manual consent record. */
  privacyFormType: PrivacyConsentFormType | null;
  privacyDispatchFailed: boolean;
  kycStatus: KycStatus | null;
  kycCompleted: boolean;
  kycFirstSmsAt: string | null;
  kycLastSmsAt: string | null;
  kycSmsCount: number;
  riskLevel: string | null;
  riskScore: number | null;
}

export interface AmlDashboardEntity {
  applicationCommercialEntityId: number;
  legalName: string | null;
  abn: string | null;
  acn: string | null;
  roles: string;
  /** Null when KYB has never run for the entity. */
  latestKybRunId: number | null;
  kybStatus: KybResultStatus | null;
  kybCompleted: boolean;
  kybStartedAt: string | null;
  kybCompletedAt: string | null;
  riskLevel: string | null;
  riskScore: number | null;
}

export interface AmlDashboardApplication {
  applicationId: number;
  brokerAppId: string | null;
  borrowingEntity: string | null;
  stageName: string | null;
  createdAt: string | null;
  amlOfficerUserId: number | null;
  amlOfficerName: string | null;
  amlOfficerAssignedAt: string | null;
  individualCount: number;
  privacyCompletedCount: number;
  kycCompletedCount: number;
  /** Entities that have a KYB run; entities never sent to KYB are not counted. */
  kybEntityCount: number;
  kybCompletedCount: number;
  highestRiskLevel: string | null;
  individuals: AmlDashboardIndividual[];
  entities: AmlDashboardEntity[];
}

export type AmlDashboardResponse = PaginablePayloadApiResponse<AmlDashboardApplication>;

export interface AmlOfficerCandidate {
  UserId: number;
  GivenName: string | null;
  FamilyName: string | null;
  Email: string;
}

export type GetAmlOfficerCandidatesFn = () => Observable<AmlOfficerCandidate[]>;
